import { z } from 'zod'
import { localeSchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import type { CostObjectProjectRef } from './cost-objects.js'
import type { OrgUnitRef } from './org-units.js'
import {
  type CustomerRef,
  type DocumentBuyer,
  type DocumentLine,
  type InvoiceStatus,
  invoiceLineInputSchema,
  type UserRef,
} from './sales.js'

// ── quotations ─────────────────────────────────────────────────────────────────
// Price offers to a customer. Not tax documents: no stock movement and no journal entry, ever.
// EXPIRED is derived on read (valid-until date before today, Asia/Muscat), never stored.

export const quotationStatusSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED', 'CONVERTED'])
export type QuotationStatus = z.infer<typeof quotationStatusSchema>

const quotationFields = z.object({
  customerId: z.uuid(),
  quotationDate: z.iso.date(),
  /** Defaults to quotation date + the `quotationValidDays` sales setting (30 unless changed). */
  validUntil: z.iso
    .date()
    .nullish()
    .transform((v) => v ?? null),
  reference: optionalText(100),
  notes: optionalText(2000),
  /**
   * A running CUSTOMER project of the quotation's customer this offer is for (extra work quoted on
   * site); converting it gives an invoice tagged with the project. Not the project the quotation
   * starts: that is `startedProject`.
   */
  projectId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  /**
   * The owning org unit (ADR-025, BR-SAL-019). Create: omitted = the creator's home unit, null = none.
   * Update: omitted keeps the current unit, only an explicit null clears it. A newly chosen unit must be
   * active; a creator whose scope is not ALL must choose one inside it.
   */
  orgUnitId: z.uuid().nullish(),
  /** The salesperson (ADR-049): carried to the invoice it converts to. Omitted = the customer's, null = none. */
  salespersonId: z.uuid().nullish(),
  lines: z.array(invoiceLineInputSchema).min(1, 'Add at least one line').max(500),
})

const validUntilNotBefore = (q: { quotationDate: string; validUntil: string | null }) =>
  q.validUntil === null || q.validUntil >= q.quotationDate
const validUntilIssue = { path: ['validUntil'], message: 'Valid-until cannot be before the quotation date' }

export const quotationInputSchema = quotationFields.refine(validUntilNotBefore, validUntilIssue)
export type QuotationInput = z.input<typeof quotationInputSchema>
/** Edits confirm the version the user read; editing a rejected quotation returns it to PENDING. */
export const quotationUpdateSchema = quotationFields
  .extend({ version: versionSchema })
  .refine(validUntilNotBefore, validUntilIssue)
export type QuotationUpdate = z.input<typeof quotationUpdateSchema>

/**
 * Approving and rejecting confirm the version the approver read: a quotation edited since (still
 * PENDING, a new version) is a 409, so nobody approves content they have not seen.
 */
export const approveQuotationSchema = z.object({ version: versionSchema })
export type ApproveQuotationInput = z.input<typeof approveQuotationSchema>

export const rejectQuotationSchema = z.object({ reason: optionalText(500), version: versionSchema })
export type RejectQuotationInput = z.input<typeof rejectQuotationSchema>

/** Turns an approved quotation into a draft sales invoice (lines and totals recomputed). */
export const convertQuotationSchema = z.object({
  /** Defaults to today (Asia/Muscat). */
  invoiceDate: z.iso.date().optional(),
  /** Defaults to invoice date + the tenant's payment terms. */
  dueDate: z.iso
    .date()
    .nullish()
    .transform((v) => v ?? null),
  warehouseId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  /** Required to convert a quotation whose valid-until date has passed. */
  confirmExpired: z.boolean().default(false),
})
export type ConvertQuotationInput = z.input<typeof convertQuotationSchema>

/**
 * "Mark lost" (Q6): an APPROVED quotation the customer declined or that was superseded becomes
 * REJECTED with a reason (`quotations.approve`). A revised offer is a copy (`/quotations/new?from=`).
 */
export const markQuotationLostSchema = z.object({
  reason: z.string().trim().min(1, 'Say why the offer was lost').max(500),
})
export type MarkQuotationLostInput = z.input<typeof markQuotationLostSchema>

/**
 * `GET /sales/quotations`. `q` matches the number and reference, the customer's code, and the
 * customer's name and Arabic name with Arabic letter variants folded. Sort keys: QUOTATION_SORT_FIELDS
 * (default `quotationDate`).
 */
export const quotationListQuerySchema = pageQuerySchema.extend({
  status: quotationStatusSchema.optional(),
  /**
   * true: pending or approved quotations whose valid-until date is before today (Asia/Muscat), the
   * list's `expired` marker. false: every quotation that is not expired (with `status=PENDING`, the
   * Pending view). Left out: both.
   */
  expired: z.stringbool().optional(),
  customerId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** Quotations created by this user; `me` is the caller ("My quotations"). */
  createdBy: z.union([z.literal('me'), z.uuid()]).optional(),
  /** Quotations tagged to this project or that started it. */
  projectId: z.uuid().optional(),
})
export type QuotationListQuery = z.input<typeof quotationListQuerySchema>

/** Sort keys of the quotation list and export (`customer`: the customer's name). Ties: newest first. */
export const QUOTATION_SORT_FIELDS = ['quotationDate', 'validUntil', 'number', 'total', 'customer'] as const

/**
 * The quotation list's view tabs (pages/sales-documents.md §2.6), each the list call beside it:
 *   all        no view filter
 *   pending    `status=PENDING&expired=false`  (suggested sort `validUntil`, soonest first)
 *   approved   `status=APPROVED&expired=false` (suggested sort `validUntil`)
 *   expired    `expired=true`                  (suggested sort `-validUntil`)
 *   converted  `status=CONVERTED`
 *   rejected   `status=REJECTED`
 * The views partition the list: their counts add up to `all`.
 */
export const QUOTATION_VIEWS = ['all', 'pending', 'approved', 'expired', 'converted', 'rejected'] as const
export type QuotationView = (typeof QUOTATION_VIEWS)[number]

/** `GET /sales/quotations/summary`: the list's filters other than the view (`status`, `expired`). */
export const quotationSummaryQuerySchema = quotationListQuerySchema.pick({
  q: true,
  customerId: true,
  from: true,
  to: true,
  createdBy: true,
  projectId: true,
})
export type QuotationSummaryQuery = z.input<typeof quotationSummaryQuerySchema>

/**
 * Counts per view for the current filters (one grouped query); each `count` equals the `total` of that
 * view's list call, and `total` (pending, approved) is Σ `total` of its rows. As of `asOf`, today in
 * Asia/Muscat (expiry is judged against it).
 */
export interface QuotationSummary {
  asOf: string
  all: { count: number }
  pending: { count: number; total: string }
  approved: { count: number; total: string }
  expired: { count: number }
  converted: { count: number }
  rejected: { count: number }
}

/** `GET /sales/quotations/export` (sales.export): the list's filters, view and sort, as CSV. */
export const quotationExportQuerySchema = quotationListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend({ format: z.enum(['csv']).default('csv'), lang: localeSchema.default('en') })
export type QuotationExportQuery = z.input<typeof quotationExportQuerySchema>

export interface QuotationListItem {
  id: string
  /** QUO-001 — assigned at creation. */
  number: string
  status: QuotationStatus
  /** A pending or approved quotation whose valid-until date is before today. */
  expired: boolean
  customer: CustomerRef
  quotationDate: string
  validUntil: string
  total: string
  convertedInvoiceId: string | null
  /**
   * The invoice this quotation became (QT-H5), with its number (null while it is a draft) and status;
   * null until converted.
   */
  convertedInvoice: { id: string; number: string | null; status: InvoiceStatus } | null
  /** The user who created the quotation; null for migrated ones. */
  createdBy: UserRef | null
  /** The salesperson (ADR-049). Always sent; optional for older fixtures. */
  salesperson?: UserRef | null
  /** The project it is tagged to (extra work). Optional in the type only for older fixtures. */
  project?: CostObjectProjectRef | null
  /**
   * The project this quotation started (projects/03 §7): it is billed from the project and can no
   * longer be converted (422 QUOTATION_STARTED_PROJECT). Optional in the type only for older fixtures.
   */
  startedProject?: CostObjectProjectRef | null
}

export type QuotationLine = DocumentLine

export interface Quotation extends QuotationListItem {
  /** The buyer block for the print (detail only; optional in the type because test fixtures predate it). */
  buyer?: DocumentBuyer
  pricesIncludeTax: boolean
  reference: string | null
  notes: string | null
  gross: string
  discount: string
  subtotal: string
  tax: string
  lines: QuotationLine[]
  rejectReason: string | null
  approvedAt: string | null
  rejectedAt: string | null
  convertedAt: string | null
  /**
   * The owning org unit (ADR-025); null when none. Always sent by the API; optional in the type only so
   * older fixtures still compile.
   */
  orgUnit?: OrgUnitRef | null
  version: number
  createdAt: string
  updatedAt: string
}
